import { RadioGroup as RadioGroupPrimitive } from 'radix-ui';
import * as React from 'react';

import { cn } from '@/_shared/lib/utils';

type RadioSize = 'md' | 'lg';

/** Set on RadioGroup, read by RadioGroupItem so every dot in a group shares one size. */
const RadioSizeContext = React.createContext<RadioSize>('md');

const DOT_CLASSES: Record<RadioSize, { control: string; dot: string }> = {
  // md (16px) gets my-0.5 to drop onto the text-sm line box; lg (20px) already matches it.
  md: { control: 'my-0.5 size-4', dot: 'size-2' },
  lg: { control: 'size-5', dot: 'size-2.5' },
};

/**
 * Single-select radio group (built on the unified `radix-ui` RadioGroup, restyled to Lookout tokens).
 * `RadioGroup.Root` gives `role="radiogroup"` + arrow-key navigation for free. `size` (md = 16px ·
 * lg = 20px) is shared to every item via context.
 *
 * `RadioGroupItem` is a **full-row** option (this app renders radios as labeled rows, not bare dots):
 * the whole row is the `role="radio"` control — full-row click, a `focus-ring` (offset ring, like
 * Button/Switch) on the
 * row, selected → a blue `bg-accent` row + a filled `bg-primary` radio with a white dot (like the checkbox). Pass the row content as
 * children; the dot sits on the leading (left) edge.
 */
function RadioGroup({
  className,
  size = 'md',
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Root> & { size?: RadioSize }): React.ReactElement {
  return (
    <RadioSizeContext.Provider value={size}>
      <RadioGroupPrimitive.Root className={cn('grid gap-0.75', className)} {...props} />
    </RadioSizeContext.Provider>
  );
}

function RadioGroupItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Item>): React.ReactElement {
  const size = React.useContext(RadioSizeContext);
  return (
    <RadioGroupPrimitive.Item
      className={cn(
        // `outline-hidden`, NOT `outline-none`: both drop the outline, but `outline-none` sets
        // `--tw-outline-style: none`, which every later `outline-*` utility reads — so a
        // `focus-visible:outline-2` on this same element silently resolves to `outline-style: none`.
        // `outline-hidden` also re-adds a transparent 2px outline under `forced-colors`, which is the
        // focus indicator that survives High Contrast stripping the ring's box-shadow.
        'group flex w-full items-start gap-3 rounded-md px-2.5 py-2 text-left text-sm outline-hidden transition-colors',
        // Title/description keep their full (foreground) color always — only the row background reacts to hover.
        // Hover tint is gated to unchecked rows so a selected (bg-accent) row keeps its tint on hover.
        'text-foreground data-[state=unchecked]:hover:bg-muted',
        // The same values as the `focus-ring` utility, spelled out as `focus-visible:ring-*` classes so a
        // block-level pattern can suppress them with `focus-visible:ring-0 focus-visible:ring-offset-0` and
        // draw ONE ring around a larger block — `cn`'s merge can only dedupe utilities it can parse, and it
        // cannot see inside `focus-ring`. Mirrors `checkbox.tsx`, which is spelled out for the same reason.
        // The utility's forced-colors half lives in `outline-hidden` above, not here.
        'focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        'data-[state=checked]:bg-accent data-[state=checked]:text-foreground',
        'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent',
        className,
      )}
      {...props}
    >
      <span
        className={cn(
          'flex shrink-0 items-center justify-center rounded-full border border-input bg-background transition-colors group-data-[state=checked]:border-primary group-data-[state=checked]:bg-primary',
          DOT_CLASSES[size].control,
        )}
      >
        <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
          <span className={cn('rounded-full bg-primary-foreground', DOT_CLASSES[size].dot)} />
        </RadioGroupPrimitive.Indicator>
      </span>
      {children}
    </RadioGroupPrimitive.Item>
  );
}

export { RadioGroup, RadioGroupItem };
